/**
 * The story-level dub reads a surface needs from the dubbing data source.
 *
 * Deliberately the one place Creator Studio's dub surfaces reach the dubbing
 * repository, so new dub surfaces add no new direct caller of it.
 */
import { useDatoClient, useUserIdentity } from '@src/stores-v2/StoreContext';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { useCallback, useEffect, useState } from 'react';

import { useVoiceActivation } from './useVoiceActivation';

import type { ResolvedDubManifest } from '@src/repositories/DubbingRepository';

/**
 * Which of a story's languages already have a dub, plus whether that list is an
 * answer yet rather than the empty value it holds while the request is out.
 *
 * `settled` matters to anything that decides from the list: an empty list is
 * indistinguishable from "this story has no dubs", so a press landing in the
 * loading window would silently act on the wrong language. A failed request and
 * a story with dubbing switched off both settle too, because nothing further is
 * going to arrive.
 */
export interface DubbedLanguagesRead {
  languages: string[];
  settled: boolean;
  /**
   * PLAT-11704 — WHICH story and gate the current `languages` answer for. A
   * consumer that hydrates from a URL compares this against the story and gate
   * of its current render, so a list left over from the previous story, or from
   * the "dubbing off" moment while flags load, never validates a
   * `voice=dubbed` link. `null` until the first answer.
   */
  settledFor: { storyId: string | null; enabled: boolean } | null;
  /**
   * Read the list again (PLAT-11440).
   *
   * The list is read once when a surface opens, which is enough while every run
   * that surface can start acts on a language already in it. Starting a FIRST
   * dub adds a language to the list on the server, and nothing here observes
   * that, so a surface that can start such a run asks for the re-read itself.
   *
   * Stable across renders, and consumers that never call it are read exactly as
   * they were before this existed.
   */
  refresh: () => void;
}

/**
 * Gated on the same combination every dub entry point uses — the runtime flag
 * and the showcase's own AI Voices setting — so a story with dubbing switched
 * off reports nothing rather than a stale list.
 *
 * Advisory: a failed read yields an empty list and never throws, so a surface
 * that cannot answer says nothing.
 */
export function useDubbedLanguagesRead(
  storyId: string | null,
): DubbedLanguagesRead {
  const datoClientStore = useDatoClient();
  const userIdentity = useUserIdentity();
  const flags = useFlagsCombination(userIdentity.currentRole);
  const { dubbingEnabled } = useVoiceActivation();
  const enabled = !!flags.enableAiVoices && dubbingEnabled;
  const [languages, setLanguages] = useState<string[]>([]);
  const [settled, setSettled] = useState(false);
  const [settledFor, setSettledFor] =
    useState<DubbedLanguagesRead['settledFor']>(null);
  // Bumped by `refresh` to ask the effect below for another read. It never
  // moves on its own, so a consumer that does not call `refresh` reads once,
  // exactly as it did before this existed.
  const [refreshToken, setRefreshToken] = useState(0);
  const refresh = useCallback(() => setRefreshToken(token => token + 1), []);

  useEffect(() => {
    if (!storyId || !enabled) {
      setLanguages([]);
      // Nothing is going to arrive, and leaving this false would hold every
      // consumer's "safe to act" gate shut forever.
      setSettled(true);
      setSettledFor({ storyId, enabled });
      return undefined;
    }
    const controller = new AbortController();
    setSettled(false);
    void datoClientStore.dubbingRepository
      .listDubbedLanguages({ storyId, signal: controller.signal })
      .then(next => {
        setLanguages(next);
        setSettled(true);
        setSettledFor({ storyId, enabled });
      })
      .catch(() => {
        /* advisory — a surface that cannot answer stays silent */
        setSettled(true);
        setSettledFor({ storyId, enabled });
      });
    return () => controller.abort();
  }, [storyId, enabled, datoClientStore, refreshToken]);

  return { languages, settled, settledFor, refresh };
}

/**
 * Which of a story's languages already have a dub, as a plain list. Consumers
 * that must also know whether the list has arrived use the read above.
 */
export function useDubbedLanguages(storyId: string | null): string[] {
  return useDubbedLanguagesRead(storyId).languages;
}

/**
 * The dub manifest fetch a surface hands into the run sequence.
 *
 * A stable callback rather than a call, because the sequence performs the fetch
 * itself, after its own guards. Answers `null` only when the server says the dub
 * does not exist; a network failure rejects instead, and the sequence leans on
 * that distinction to tell "never had it" from "had it and lost it".
 */
export function useDubManifestFetch(): (params: {
  storyId: string;
  language: string;
}) => Promise<ResolvedDubManifest | null> {
  const datoClientStore = useDatoClient();
  return useCallback(
    params => datoClientStore.dubbingRepository.getDubManifest(params),
    [datoClientStore],
  );
}

/**
 * PLAT-11704 — the dubbed-language list, minus a selected language whose dub
 * turns out not to be playable.
 *
 * The story-level list says a manifest exists; the per-language read is
 * stricter (it also wants the rendition audio) and answers `null` when it
 * cannot serve the dub. A language in the list but with no servable manifest
 * would otherwise show Voice / Music controls and a "Review Dub" microphone for
 * audio that never plays. Only the SELECTED language is confirmed — one
 * request, not one per language — and only while the list names it. Until the
 * answer lands the list is trusted, so the controls do not flash off and on.
 */
export function usePlayableDubbedLanguages(input: {
  storyId: string | null;
  languages: readonly string[];
  selectedLanguage: string | null;
  enabled: boolean;
}): string[] {
  const { storyId, languages, selectedLanguage, enabled } = input;
  const fetchManifest = useDubManifestFetch();
  const [unplayable, setUnplayable] = useState<{
    storyId: string;
    language: string;
  } | null>(null);
  const listed =
    enabled &&
    !!storyId &&
    !!selectedLanguage &&
    languages.includes(selectedLanguage);

  useEffect(() => {
    if (!listed || !storyId || !selectedLanguage) return undefined;
    const controller = new AbortController();
    void fetchManifest({ storyId, language: selectedLanguage })
      .then(manifest => {
        if (controller.signal.aborted) return;
        setUnplayable(
          manifest === null ? { storyId, language: selectedLanguage } : null,
        );
      })
      .catch(() => {
        /* a network failure is not "no dub" — keep trusting the list */
      });
    return () => controller.abort();
  }, [listed, storyId, selectedLanguage, languages, fetchManifest]);

  if (
    unplayable &&
    unplayable.storyId === storyId &&
    languages.includes(unplayable.language)
  ) {
    return languages.filter(l => l !== unplayable.language);
  }
  return languages as string[];
}
